import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PANEL_RESIZE_PROFILES, ITINERARY_PANEL } from "../../../config/itineraryPanel.configuration";
import { FLOATING_MENU_FAB_ROW_ELEMENT_ID } from "../../FloatingActions/FloatingActionBurst/floatingActionBurst.configuration";
import {
    measureFloatingActionRowBottomPx,
    measureMaximizedPanelHeightVh,
    resolveChromeMinHeightVh,
    resolveViewportHeightPx,
} from "./panelResize.tools";
import type { PanelResizeLayout } from "./panelResize.types";

export interface UsePanelLayoutBoundsOptions {

    /** DOM id of the FAB row used to measure the maximized anchor. */
    "fabRowElementId"?: string;

    /** Gap (px) between the FAB row bottom and the maximized panel top. */
    "panelTopGapPx": number;

    /** Profile used to derive chrome minimum height for layout bounds. */
    "chromeProfileId"?: keyof typeof PANEL_RESIZE_PROFILES;

    /**
     * Bumps whenever floating chrome moves vertically without resizing (e.g. search
     * bar `top` transitions). Home passes `searchBarsBottomPx` for live FAB tracking.
     */
    "layoutRevision"?: number | null;
}

export interface UsePanelLayoutBoundsResult {
    "containerRef":           React.RefObject<HTMLDivElement | null>;
    "maximizedPanelHeightVh": number;
    "chromeMinHeightVh":      number;
    "panelResizeLayout":      PanelResizeLayout;
}

/**
 * Measures FAB-row and viewport bounds for the shared bottom panel resize module.
 *
 * @param options - Container and FAB row measurement configuration.
 * @returns Container ref, maximized height, and grouped layout props for children.
 */
export const usePanelLayoutBounds = ( options: UsePanelLayoutBoundsOptions ): UsePanelLayoutBoundsResult => {
    const {
        fabRowElementId = FLOATING_MENU_FAB_ROW_ELEMENT_ID,
        panelTopGapPx,
        chromeProfileId = "itineraryDetails",
        layoutRevision = null,
    } = options;

    const containerRef = useRef<HTMLDivElement | null>( null );
    const [ fabRowBottomPx, setFabRowBottomPx ] = useState<number | null>( null );
    const [ viewportHeightPx, setViewportHeightPx ] = useState<number>( () => resolveViewportHeightPx() );

    useEffect( () => {
        const recomputeFabRowBottom = (): void => {
            setFabRowBottomPx( measureFloatingActionRowBottomPx( fabRowElementId ) );
        };

        const recomputeViewport = (): void => {
            setViewportHeightPx( resolveViewportHeightPx() );
            recomputeFabRowBottom();
        };

        recomputeViewport();

        const fabRowElement = document.getElementById( fabRowElementId );
        let resizeObserver: ResizeObserver | null = null;

        if ( fabRowElement !== null && typeof ResizeObserver !== "undefined" ) {
            resizeObserver = new ResizeObserver( () => {
                recomputeFabRowBottom();
            } );
            resizeObserver.observe( fabRowElement );
        }

        window.addEventListener( "resize", recomputeViewport );
        window.visualViewport?.addEventListener( "resize", recomputeViewport );
        window.visualViewport?.addEventListener( "scroll", recomputeFabRowBottom );

        return () => {
            window.removeEventListener( "resize", recomputeViewport );
            window.visualViewport?.removeEventListener( "resize", recomputeViewport );
            window.visualViewport?.removeEventListener( "scroll", recomputeFabRowBottom );
            resizeObserver?.disconnect();
        };
    }, [ fabRowElementId, layoutRevision ] );

    const maximizedPanelHeightVh = useMemo( () => {
        return measureMaximizedPanelHeightVh(
            fabRowBottomPx,
            viewportHeightPx,
            panelTopGapPx,
            ITINERARY_PANEL.panelMaxHeightVh,
        );
    }, [ fabRowBottomPx, panelTopGapPx, viewportHeightPx ] );

    const resolveMaximizedPanelHeightVh = useCallback( (): number => {
        const viewportHeightPxLive = resolveViewportHeightPx();
        const panelBottomPx = containerRef.current?.getBoundingClientRect().bottom ?? null;

        return measureMaximizedPanelHeightVh(
            measureFloatingActionRowBottomPx( fabRowElementId ),
            viewportHeightPxLive,
            panelTopGapPx,
            ITINERARY_PANEL.panelMaxHeightVh,
            panelBottomPx,
        );
    }, [ fabRowElementId, panelTopGapPx ] );

    const chromeMinHeightVh = useMemo( () => {
        return resolveChromeMinHeightVh( PANEL_RESIZE_PROFILES[chromeProfileId], viewportHeightPx );
    }, [ chromeProfileId, viewportHeightPx ] );

    const panelResizeLayout = useMemo<PanelResizeLayout>( () => ( {
        containerRef,
        maximizedPanelHeightVh,
        resolveMaximizedPanelHeightVh,
        "chromeMinHeightVh": chromeMinHeightVh,
    } ), [ chromeMinHeightVh, maximizedPanelHeightVh, resolveMaximizedPanelHeightVh ] );

    return {
        containerRef,
        maximizedPanelHeightVh,
        chromeMinHeightVh,
        panelResizeLayout,
    };
};
